import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { setModels } from "@/Context/XktContext/Reducer";
import { XktContext } from "@/Context/XktContext/XktContextProvider";
import { cn } from "@/lib/utils";
import Model from "@/Models/Model";
import { Check, ChevronRight, ChevronsUpDown } from "lucide-react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

interface ModelComboboxProps {
    models: Model[];
}

const ModelCombobox = ({ models }: ModelComboboxProps) => {
    const [open, setOpen] = useState(false);
    const [values, setValues] = useState<string[]>([]);
    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
    const [search, setSearch] = useState("");

    const { dispatch } = useContext(XktContext);
    const { t } = useTranslation();

    const grouped = useMemo(() => {
        const groups: Record<string, Model[]> = {};

        for (const m of models) {
            const hasUnderscore = m.modelName.includes("_");
            const groupName = hasUnderscore ? m.modelName.split("_")[0] : "Demo Models";

            if (!groups[groupName]) groups[groupName] = [];
            groups[groupName].push(m);
        }

        const sortedGroupNames = Object.keys(groups).sort((a, b) => a.localeCompare(b));

        return sortedGroupNames.map((name) => {
            const fullList = groups[name];

            const visibleList = search.trim()
                ? fullList.filter((m) => m.modelName.toLowerCase().includes(search.toLowerCase()))
                : fullList;

            const total = fullList.length;
            const loaded = fullList.filter((m) => values.includes(m.modelId)).length;

            return {
                name,
                models: visibleList,
                allModels: fullList,
                total,
                loaded,
                isVisible: visibleList.length > 0,
            };
        });
    }, [models, values, search]);

    const selectedModels = models.filter((m) => values.includes(m.modelId));

    const renderValue = () => {
        if (selectedModels.length === 0) return t("selectModelsPlaceholder");
        return selectedModels.map((x) => x.modelName).join(", ");
    };

    useEffect(() => {
        const selected = models.filter((m) => values.includes(m.modelId));
        dispatch(setModels(selected));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);

    const toggleValue = (modelId: string) => {
        setValues((prev) => (prev.includes(modelId) ? prev.filter((v) => v !== modelId) : [...prev, modelId]));
    };

    const selectEntireGroup = (groupName: string) => {
        const groupModels = grouped.find((g) => g.name === groupName)?.allModels ?? [];
        const ids = groupModels.map((m) => m.modelId);
        setValues((prev) => Array.from(new Set([...prev, ...ids])));
    };

    const deselectEntireGroup = (groupName: string) => {
        const groupModels = grouped.find((g) => g.name === groupName)?.allModels ?? [];
        const ids = groupModels.map((m) => m.modelId);
        setValues((prev) => prev.filter((id) => !ids.includes(id)));
    };

    const toggleGroup = (name: string) => {
        setCollapsedGroups((prev) => ({
            ...prev,
            [name]: !prev[name],
        }));
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-[250px] flex justify-between"
                >
                    <span className="w-[190px] text-left text-nowrap text-ellipsis overflow-hidden">
                        {renderValue()}
                    </span>
                    <ChevronsUpDown className="opacity-50" />
                </Button>
            </PopoverTrigger>

            <PopoverContent className="w-[370px] p-0">
                <Command>
                    <CommandInput
                        placeholder={t("searchModelPlaceholder")}
                        className="h-9"
                        value={search}
                        onValueChange={setSearch}
                    />

                    <CommandList>
                        {search.trim().length > 0 && grouped.every((g) => !g.isVisible) && (
                            <CommandEmpty>{t("noModelFound")}</CommandEmpty>
                        )}

                        {grouped.map((group) =>
                            group.isVisible ? (
                                <div key={group.name}>
                                    <div
                                        className="flex justify-between items-center px-2 py-1 cursor-pointer bg-muted/40"
                                        onClick={() => toggleGroup(group.name)}
                                    >
                                        <span className="font-medium">
                                            {group.name} [{group.loaded}/{group.total}]
                                        </span>

                                        <div className="flex items-center gap-3">
                                            <div className="flex items-center gap-3">
                                                {group.loaded < group.total && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            selectEntireGroup(group.name);
                                                        }}
                                                        className="text-xs text-muted-foreground hover:underline"
                                                    >
                                                        Select All
                                                    </button>
                                                )}

                                                {group.loaded > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            deselectEntireGroup(group.name);
                                                        }}
                                                        className="text-xs text-muted-foreground hover:underline"
                                                    >
                                                        Deselect All
                                                    </button>
                                                )}

                                                <ChevronRight
                                                    className={cn(
                                                        "transition-transform",
                                                        collapsedGroups[group.name] ? "" : "rotate-90"
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {!collapsedGroups[group.name] && (
                                        <CommandGroup>
                                            {group.models.map((model) => (
                                                <CommandItem
                                                    key={model.modelId}
                                                    value={model.modelName}
                                                    onSelect={() => toggleValue(model.modelId)}
                                                >
                                                    <span className="break-all whitespace-normal">
                                                        {model.modelName}
                                                    </span>

                                                    <Check
                                                        className={cn(
                                                            "ml-auto",
                                                            values.includes(model.modelId) ? "opacity-100" : "opacity-0"
                                                        )}
                                                    />
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    )}
                                </div>
                            ) : null
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

export default ModelCombobox;
